import {
    CodeConfig,
    CodeNameInfoConfig,
    CodeUniqueConfig,
    Config,
    MERCATOR_CODE,
    RESOLUTIONS,
    verifyExtentLonLatWgs84,
    verifyUrl,
    WGS84_CODE
} from "./config";
import {StyleConfig} from "./StyleConfig";
import * as LZString from "lz-string";
import {
    DeviceVisibilityEnum,
    IndicatorChartTypeEnum,
    LayerGroupTypeEnum,
    LayerIdentifyType,
    LayerTypeEnum,
    PeriodicityEnum,
    ProtocolEnum,
    TimeScaleEnum
} from "../enum/enum";
import {clone, isBoolean, isNumber, isString, nn, nnTrim, prefixUrl, toNumber} from "../util";
import {dateFromStr, dateFromYearMonthDay, TimeCalculator, verifyDateString} from "../time/TimeCalculator";
import parseCSSColor from "parse-css-color";
import {Base} from "../Base";

/**
 * Configuration of a Tumbo application
 * @abstract
 * @extends Base
 */
class MainConfig extends Base {
    /**
     *
     * @param config {Object} The config object
     */
    constructor(config) {
        super();
        this._initialConfig = config;
        this.reset();
    }

    getClassName() {
        return "MainConfig";
    }

    _clone(obj) {
        return JSON.parse(JSON.stringify(obj));
    }

    /**
     * Reset this config to its original state from the original config object
     */
    reset() {
        CodeConfig._CODES_BY_CLASS = {};
        this.config = this._clone(this._initialConfig);
        this._loadAllServiceWarningMessages();
        this._loadAllStyles();
    }

    _loadAllServiceWarningMessages() {
        this.allServiceWarningMessages = {};
        this.getAllServiceWarningMessages().forEach(wm => {
            if (!this.allServiceWarningMessages[wm.getServiceCode()])
                this.allServiceWarningMessages[wm.getServiceCode()] = [];
            this.allServiceWarningMessages[wm.getServiceCode()].push(wm)
        });
    }

    /**
     * All the available styles
     * @return {Object<string,StyleConfig>}
     */
    _loadAllStyles() {
        if (this.allStyles)
            return;
        this.allStyles = {};
        if (this.config.styles) {
            for (let s in this.config.styles) {
                const style = new StyleConfig(this.config.styles[s]);
                this.allStyles[style.getCode()] = style;
            }
        }
    }

    getStyleByCode(code) {
        if (!nnTrim(code))
            return null;
        this._loadAllStyles();
        return this.allStyles[nnTrim(code)];
    }

    /**
     * The name
     * @return {string}
     */
    getName() {
    }

    /**
     * The scale id param to pass to the download indicator service
     * @return {string}
     */
    getScaleId() {
        return this.getCode().split('_')[0];
    }

    /**
     * The code. Should be unique among deployments
     * @return {string}
     */
    getCode() {
    }

    /**
     * Stats post URL
     * @return {string}
     */
    getStatsPostUrl() {
        return prefixUrl(this._getStatsPostUrlProperty());
    }

    /**
     * Contact post URL
     * @return {string}
     */
    getContactPostUrl() {
        return prefixUrl(this._getContactPostUrlProperty());
    }

    /**
     * Help contents URL
     * @return {string}
     */
    getHelpContentsUrl() {
        return prefixUrl(this._getHelpContentsUrlProperty());
    }

    /**
     * Should be overridden to return the stats post URL
     * @private
     */
    _getStatsPostUrlProperty() {
    }

    /**
     * Should be overridden to return the contact post URL
     * @private
     */
    _getContactPostUrlProperty() {
    }

    /**
     * Should be overridden to return the help contents URL
     * @private
     */
    _getHelpContentsUrlProperty() {
    }

    /**
     * Get all general warning messages
     * @return {Array<WarningMessageConfig>}
     */
    getGeneralWarningMessages() {
        if (this.config.warningMessages && this.config.warningMessages.general) {
            return this.config.warningMessages.general.map(g => new WarningMessageConfig(g));
        }
        return []
    }

    /**
     * Get all help contents
     * @return {Object<Object>}
     */
    getHelpContents() {
        const res = {};
        if(this.config.helpTopics)
            Object.keys(this.config.helpTopics).forEach(code=>{
                const help = this.config.helpTopics[code];
                const content = nnTrim(help.short_text);
                const title = nnTrim(help.title);
                if(content && title)
                    res[code]=help;
        });
        return res;
    }

    /**
     * Get all service warning messages
     * @return {Array<ServiceWarningMessageConfig>}
     */
    getAllServiceWarningMessages() {
        if (this.config.warningMessages && this.config.warningMessages.service) {
            return this.config.warningMessages.service.map(g => new ServiceWarningMessageConfig(g, this.allLayerServices));
        }
        return [];
    }

    /**
     * Verify this configuration and return a list of errors
     * @return {Array<string>}
     */
    verify() {
        CodeUniqueConfig._ALL_CODES = {};

        let errors = [];

        if (!this.getName() || this.getName().length === 0) {
            errors.push(this.getClassName() + ': name is mandatory');
        }

        if (!this.getCode() || this.getCode().length === 0) {
            errors.push(this.getClassName() + ': code is mandatory');
        }

        if (this.getStatsPostUrl() && this.getStatsPostUrl().length > 0) {
            if (!verifyUrl(this.getStatsPostUrl())) {
                errors.push(this.getClassName() + ': Invalid url on statsPostUrl');
            }
        }

        if (this.getHelpContentsUrl() && this.getHelpContentsUrl().length > 0) {
            if (!verifyUrl(this.getHelpContentsUrl())) {
                errors.push(this.getClassName() + ': Invalid url on helpContentsUrl');
            }
        }

        this.getGeneralWarningMessages().forEach(w => errors = errors.concat(w.verify()));
        this.getAllServiceWarningMessages().forEach(w => errors = errors.concat(w.verify()));

        return errors;
    }

    /**
     * Override this config
     * @param runtimeConfig {RuntimeConfig} The configuration override
     */
    override(runtimeConfig) {
    }
}

/**
 * The configuration of any app based on indicators
 * @extends MainConfig
 */
class IndicatorBasedMainConfig extends MainConfig {
    /**
     *
     * @param config {Object} The config object
     * @param productSettings {Object} Codes for disabled products
     */
    constructor(config, productSettings = undefined) {
        super(config);
        this.productSettings = productSettings;
        this._updateProductsFromSettings();
    }

    _updateProductsFromSettings(){
        if(this.productSettings)
            Object.keys(this.productSettings).forEach(code=>{
                const pSettings = this.productSettings[code];
                const product = this.allProducts[code];
                if(pSettings && product){
                    if(!!pSettings.disabled)
                        product.disable();
                    else product.enable();
                    product.clearMissingTimesteps();
                    if(pSettings.missing)
                        product.addMissingTimesteps(pSettings.missing)
                    product.clearProvisionalTimesteps();
                    if(pSettings.provisional)
                        product.addProvisionalTimesteps(pSettings.provisional)
                    product.clearDeficientTimesteps();
                    if(pSettings.deficient)
                        product.addDeficientTimesteps(pSettings.deficient)
                }
            })
    }

    getClassName() {
        return "IndicatorBasedMainConfig";
    }

    reset(productSettings) {
        super.reset();
        this._loadAllLayerServices();
        this._loadAllProducts();
        this.productSettings = productSettings;
        this._updateProductsFromSettings();
    }

    _loadAllLayerServices() {
        this.allLayerServices = {};
        if (this.config.services) {
            for (let d in this.config.services) {
                const layerService = new LayerServiceConfig(this.config.services[d], this.allServiceWarningMessages);
                this.allLayerServices[layerService.getCode()] = layerService;
            }
        }
    }

    _loadAllProducts() {
        this.allProducts = {};
        if (this.config.products) {
            for (let p in this.config.products) {
                const product = new ProductConfig(this.config.products[p], this.getScaleId());
                this.allProducts[product.getCode()] = this.disabledProductCodes && this.disabledProductCodes.indexOf(product.getCode()) >= 0
                    ? product.disable()
                    : product.enable();
            }
        }
    }

    /**
     * Data series URL
     * @return {string}
     */
    getDataSeriesUrl() {
        return prefixUrl(this._getDataSeriesUrlProperty());
    }

    /**
     * Data regional series URL
     * @return {string}
     */
    getRegionDataSeriesUrl() {
        return prefixUrl(this._getRegionDataSeriesUrlProperty());
    }

    /**
     * Should be overridden to return the data series URL
     * @private
     */
    _getDataSeriesUrlProperty() {
    }

    /**
     * Should be overridden to return the regional data series URL
     * @private
     */
    _getRegionDataSeriesUrlProperty() {
    }

    /**
     * Feature data series URL
     * @return {string}
     */
    getFeatureDataSeriesUrl() {
        return prefixUrl(this._getFeatureDataSeriesUrlProperty());
    }

    /**
     * Should be overridden to return the feature data series URL
     * @private
     */
    _getFeatureDataSeriesUrlProperty() {
    }

    verify() {
        let errors = super.verify();
        if (this.getDataSeriesUrl() && this.getDataSeriesUrl().length > 0) {
            if (!verifyUrl(this.getDataSeriesUrl())) {
                errors.push(this.getClassName() + ': Invalid url on dataSeriesUrl');
            }
        }
        if (this.getFeatureDataSeriesUrl() && this.getFeatureDataSeriesUrl().length > 0) {
            if (!verifyUrl(this.getFeatureDataSeriesUrl())) {
                errors.push(this.getClassName() + ': Invalid url on featureDataSeriesUrl');
            }
        }
        return errors;
    }
}

/**
 * Runtime configuration of a Tumbo application
 * Stores data that override the initial config
 * @abstract
 * @extends Base
 */
class RuntimeConfig extends Base {

    /**
     * @param code {string} Unique identifier for this runtime config
     * @param config {Object} The config object
     */
    constructor(code, config) {
        super();
        this.code = code;
        this.config = config;
        if (!this.config) {
            this.clean();
        }
    }

    getClassName() {
        return "RuntimeConfig";
    }

    /**
     * Unique identifier for this runtime config
     * @return {string}
     */
    getCode() {
        return this.code;
    }

    /**
     * Clean this runtime config
     */
    clean() {
    }

    /**
     * Get the configuration object suitable to override MainConfig
     * @return {Object}
     */
    getConfigOverrideObject() {
        return this.config;
    }

    /**
     * Get the serialized version of this runtime config, suitable to store
     * @return {string}
     */
    getSerializedConfig() {
        // return (JSON.stringify(this.getConfigOverrideObject()));
        return LZString.compressToBase64(JSON.stringify(this.getConfigOverrideObject()));
    }

    /**
     * Get the serialized version of this runtime config, suitable to use in an URL
     * @return {string}
     */
    getSharableSerializedConfig() {
        const config2Share = JSON.parse(JSON.stringify(this.getConfigOverrideObject()));
        if (config2Share.t && config2Share.t["comparator"]) {
            delete config2Share.t["comparator"];
        }
        return LZString.compressToBase64(JSON.stringify(config2Share));
    }

    /**
     * set a value for a property of a tool
     * @param toolCode {string} The tool code
     * @param propCode {string} the property code
     * @param value {*} the value of the property
     */
    setToolProperty(toolCode, propCode, value) {
        this.config.t[toolCode] = this.config.t[toolCode] || {};
        this.config.t[toolCode][propCode] = value;
    }

    /**
     * get the value for a property of a tool
     * @param toolCode {string} The tool code
     * @param propCode {string} the property code
     * @return {*} the value of the property
     */
    getToolProperty(toolCode, propCode) {
        return this.config.t && this.config.t[toolCode]
            ? this.config.t[toolCode][propCode]
            : undefined;
    }

    /**
     * Set a property value that lasts for this session (ends on close tab)
     * @param uiElemCode {string} the unique id of this UI element
     * @param prop {string} A property key
     * @param value {*} any value
     */
    setUIElementSessionProp(uiElemCode, prop, value) {
       this.setSessionProp(uiElemCode+"_"+prop, value);
    }

    /**
     * Remove a property
     * @param uiElemCode {string} the unique id of this UI element
     * @param prop {string} A property key
     */
    removeUIElementSessionProp(uiElemCode, prop) {
        this.removeSessionProp(uiElemCode + "_" + prop);
    }

    /**
     * Is there a value for a property key?
     * @param uiElemCode {string} the unique id of this UI element
     * @param prop {string} A property key
     * @return {boolean}
     */
    hasUIElementSessionProp(uiElemCode, prop) {
        return this.hasSessionProp(uiElemCode + "_" + prop);
    }

    /**
     * Get a value by property key
     * @param uiElemCode {string} the unique id of this UI element
     * @param prop {string} A property key
     * @param defValue {*} A value to return if not set
     * @return {*}
     */
    getUIElementSessionProp(uiElemCode, prop, defValue = undefined) {
        return this.getSessionProp(uiElemCode + "_" + prop, defValue);
    }

    /**
     * Set a property value that lasts for this session (ends on close tab)
     * @param prop {string} A property key
     * @param value {*} any value
     */
    setSessionProp(prop, value) {
        sessionStorage.setItem(this.code + "_" + prop, JSON.stringify({value: value}));
    }

    /**
     * Remove a property
     * @param prop {string} A property key
     */
    removeSessionProp(prop) {
        sessionStorage.removeItem(this.code + "_" + prop);
    }

    /**
     * Is there a value for a property key?
     * @param prop {string} A property key
     * @return {boolean}
     */
    hasSessionProp(prop) {
        for (let i = 0; i < sessionStorage.length; i++) {
            if (sessionStorage.key(i) === (this.code + "_" + prop))
                return true;
        }
        return false;
    }

    /**
     * Get a value by property key
     * @param prop {string} A property key
     * @param defValue {*} A value to return if not set
     * @return {*}
     */
    getSessionProp(prop, defValue = undefined) {
        const serialized = sessionStorage.getItem(this.code + "_" + prop);
        if (serialized)
            try {
                return JSON.parse(serialized).value;
            } catch (e) {
                this.logError("Error deserializing prop " + prop + ", for prop " + prop, e);
            }
        else return defValue;
    }

}

/**
 * A configuration part that overrides configuration properties
 */
class ConfigOverride extends Base {
    getClassName() {
        return "ConfigOverride";
    }

    /**
     *
     * @param config {Array<Object>} The overrides. Each element must have "start" and "end" properties (format: "MM-DD") that indicate the time validity of the override. \
     * The remaining properties are the actual overrides and property names must match the overridden properties
     * @param overridableProps {Array<String>} All the properties that are overridable (any other property will be ignored in the config)
     */
    constructor(config, overridableProps) {
        super();
        this.config = config;
        this.overridableProps = overridableProps ? overridableProps : [];
    }

    /**
     * All the overridable properties
     * @return {[]|Array<String>}
     */
    getOverridableProps() {
        return this.overridableProps;
    }

    /**
     * Does it override a property in the given date
     * @param propName {string} The property name
     * @param date {Date} The date
     * @return {boolean}
     */
    overridesProperty(propName, date) {
        if (!this.config || !date || !nnTrim(propName)
            || (this.overridableProps.length > 0 && this.overridableProps.indexOf(propName) < 0))
            return false;
        const overrideElements = this.config.filter(c => this._isIn(c, date) && Object.keys(c).indexOf(propName) >= 0);
        return overrideElements.length > 0;
    }

    /**
     * Get the overridden value for a property in a given date. Returns defaultValue if the property is not overridden for the given date
     * @param propName {string} The property name
     * @param date {Date} The date
     * @param defaultValue {*} The value to return of the property if not overridden
     * @return {*}
     */
    getValueForProperty(propName, date, defaultValue) {
        if (!date || !this.overridesProperty(propName, date))
            return defaultValue;
        const overrideElements = this.config.filter(c => this._isIn(c, date) && Object.keys(c).indexOf(propName) >= 0);
        return overrideElements[0][propName];
    }

    /**
     * Is the given property overridden, despite the date?
     * @param propName {string} The property name
     * @return {boolean}
     */
    overridesPropertyAnyDate(propName) {
        if (!this.config || !nnTrim(propName)
            || (this.overridableProps.length > 0 && this.overridableProps.indexOf(propName) < 0))
            return false;
        const overrideElements = this.config.filter(c => Object.keys(c).indexOf(propName) >= 0);
        return overrideElements.length > 0;
    }

    /**
     * Get all the overriding values for a property, despite the date
     * @param propName {string} The property name
     * @return {Array<*>}
     */
    getAllValuesForProperty(propName) {
        if (!this.overridesPropertyAnyDate(propName))
            return [];
        return this.config
            .filter(c => Object.keys(c).indexOf(propName) >= 0)
            .map(c => c[propName]);
    }

    _isIn(overrideElement, date) {
        const year = date.getFullYear();
        const startSplit = overrideElement.start.split('-');
        const startMonth = Number(startSplit[0].trim());
        const startDay = Number(startSplit[1].trim());
        const endSplit = overrideElement.end.split('-');
        const endMonth = Number(endSplit[0].trim());
        const endDay = Number(endSplit[1].trim());

        const startDate = dateFromYearMonthDay(year, startMonth, startDay);
        const endDate = dateFromYearMonthDay(year, endMonth, endDay);

        const isInside = (intervalMin, intervalMax, instant) => instant >= intervalMin && instant < intervalMax;
        if (startDate > endDate) {
            return !isInside(endDate, startDate, date);
        } else {
            return isInside(startDate, endDate, date);
        }
    }

    /**
     * Verify this configuration and return a list of errors
     * @return {Array<string>}
     */
    verify() {
        const errors = [];
        if (this.config) {
            const vefifyDate = (index, overrideElement, datePropName) => {
                if (!nnTrim(overrideElement[datePropName]))
                    errors.push("override [" + index + "]: " + datePropName + " is mandatory");
                else {
                    if (!verifyDateString(new Date().getFullYear() + '-' + nnTrim(overrideElement[datePropName])))
                        errors.push("override [" + index + "]: " + datePropName + " is invalid. format must be: MM-DD");
                }
            }
            for (let i = 0; i < this.config.length; i++) {
                vefifyDate(i, this.config[i], 'start');
                vefifyDate(i, this.config[i], 'end');
            }
        }
        return errors;
    }
}

/**
 * A general warning message for the user
 * @extends CodeUniqueConfig
 * @hideconstructor
 */
class WarningMessageConfig extends CodeUniqueConfig {
    getClassName() {
        return "WarningMessageConfig";
    }

    getMessage() {
        return this.config.message;
    }

    verify() {
        const errors = super.verify();
        if (!this.config.message || this.config.message.trim().length === 0) {
            errors.push(this.getLogId() + ": message is mandatory");
        } else {
            // if (this.getMessage().length > 600) {
            //     errors.push(this.getLogId() + ": message cannot have more than 600 chars (has " + this.getMessage().length + ")");
            // }
        }

        if (this.getStart() && this.getEnd() && this.getStart() > this.getEnd()) {
            errors.push(this.getLogId() + ": invalid dates");
        }
        return errors;
    }

    getStart() {
        return this.config.start && this.config.start.trim().length > 0
            ? dateFromStr(this.config.start)
            : undefined;
    }

    getEnd() {
        return this.config.end && this.config.end.trim().length > 0
            ? dateFromStr(this.config.end)
            : undefined;
    }

    /**
     * Should this message be shown now?
     * @return {boolean}
     */
    shouldBeShown() {
        const now = new Date();
        if (this.getStart() && this.getStart() > now)
            return false;
        if (this.getEnd() && this.getEnd() < now)
            return false;
        return true;
    }
}

/**
 * A warning message related to a certain service
 * @extends WarningMessageConfig
 * @hideconstructor
 */
class ServiceWarningMessageConfig extends WarningMessageConfig {

    getClassName() {
        return "ServiceWarningMessageConfig";
    }

    getServiceCode() {
        return this.config.srvCode;
    }

    verify() {
        const errors = super.verify();
        if (!this.config.srvCode || this.config.srvCode.trim().length === 0) {
            errors.push(this.getLogId() + ": srvCode is mandatory");
        }
        return errors;
    }
}

/**
 * The config of an element of the layer tree
 * @extends CodeUniqueNameInfoConfig
 * @hideconstructor
 */
class LayerTreeElementConfig extends CodeUniqueConfig {

    getClassName() {
        return "LayerTreeElementConfig";
    }

    /**
     * The type of the element
     * @return {LayerGroupTypeEnum}
     */
    getType() {
        return LayerGroupTypeEnum.getForCode(this.config.type);
    }

    /**
     * Is this element visible?
     * @return {boolean}
     */
    isVisible() {
        return this.config.visible === true;
    }

    /**
     * Device visibility of this element
     * @return {DeviceVisibilityEnum}
     */
    getDeviceVisibility() {
        return DeviceVisibilityEnum.getForCode(this.config.deviceVisibility);
    }

    /**
     * Opacity of this element
     * @return {number} [0 .. 1]
     */
    getOpacity() {
        return this.config.opacity !== undefined && this.config.opacity !== null ? this.config.opacity : 1;
    }

    /**
     * The render ordinal. If defined, overrides the layer tree ordinal
     * @return {number|undefined}
     */
    getRenderOrdinal() {
        return toNumber(this.config.renderOrdinal);
    }

    /**
     * This element's children
     * @return {Array<LayerTreeElementConfig>}
     */
    getChildren() {
        return [];
    }

    /**
     * Is tree element disabled?
     * @return {*}
     */
    isDisabled() {
        return this.config.disabled;
    }

    verify() {
        let errors = super.verify(this);

        if (!this.config.type || this.config.type.length === 0) {
            errors.push(this.getLogId() + ": type is mandatory");
        } else if (!LayerGroupTypeEnum.exists(this.config.type)) {
            errors.push(this.getLogId() + ": invalid type. Possible values: " + LayerGroupTypeEnum.getPossibleCodes());
        }

        if (this.config.deviceVisibility && this.config.deviceVisibility.length > 0
            && !DeviceVisibilityEnum.exists(this.config.deviceVisibility)) {
            errors.push(this.getLogId() + ": invalid deviceVisibility. Possible values: " + DeviceVisibilityEnum.getPossibleCodes());
        }

        if (this.getOpacity() !== undefined && (this.getOpacity() < 0 || this.getOpacity() > 1)) {
            errors.push(this.getLogId() + ': opacity must be a value between 0 and 1');
        }

        if (this.config.renderOrdinal !== null && this.config.renderOrdinal !== undefined) {
            const number = toNumber(this.config.renderOrdinal);
            if (isNumber(number)) {
                if (number < 0) {
                    errors.push(this.getLogId() + ": renderOrdinal must be >= 0");
                }
            } else {
                errors.push(this.getLogId() + ": renderOrdinal must be a number");
            }
        }

        if (this.config.disabled !== null && this.config.disabled !== undefined) {
            if (!isBoolean(this.config.disabled))
                errors.push(this.getLogId() + ": disabled must be a boolean");
        }

        return errors;
    }
}

/**
 * The config of a layer, which is a tree leaf
 * @extends LayerTreeElementConfig
 */
class LayerConfig extends LayerTreeElementConfig {

    static PRODUCT_DATES = {};

    static LAYER_CODES = new Set();

    /**
     *
     * @param config {Object} The config object
     * @param allServices {Object<String,LayerServiceConfig>} All the available layer services
     * @param allProducts {Object<String,ProductConfig>} All the available products
     * @param allStyles {Object<String,StyleConfig>} All the available styles
     */
    constructor(config, allServices, allProducts, allStyles) {
        super(config);
        LayerConfig.LAYER_CODES.add(this.getCode());
        this.service = allServices[this.config.srvCode];
        this.highlightService = allServices[this.config.srvCode + '_identify'];
        this.allStyles = allStyles;
        this.allServices = allServices;
        this.allProducts = allProducts;
        (this.service && this.getTimeScales() && this.getTimeScales().length > 0 ? this.getTimeScales() : [null]).forEach(ts => {
            const prod = this.getProduct(ts);
            if (prod && !LayerConfig.PRODUCT_DATES[prod.getCode()])
                LayerConfig.PRODUCT_DATES[prod.getCode()] = {
                    startDay: dateFromStr(prod.getDataFrom()),
                    endDay: dateFromStr(prod.getDataUntil()),
                    frequency: prod.getPeriodicity() ? prod.getPeriodicity().getCode() : undefined
                };
        });
    }

    getClassName() {
        return "LayerConfig";
    }

    clone(code) {
        const clonedConfig = clone(this.config);//JSON.parse(JSON.stringify(this.config));
        if(code)
            clonedConfig.code = code;
        return new this.__proto__.constructor(clonedConfig, this.allServices, this.allProducts, this.allStyles);
    }

    /**
     *
     * @return {LayerServiceConfig}
     */
    getService() {
        return this.service;
    }

    /**
     *
     * @return {Array.<LayerServiceConfig>}
     */
    getDataSeriesServices() {
        return this.config.dataSeriesServCodes ? this.config.dataSeriesServCodes.map(code=>this.allServices[nnTrim(code)]).filter(s=>!!s) : [];
    }

    /**
     * @fids {string|undefined} A comma separated list of integer feature ids
     * @return {LayerServiceConfig}
     */
    getHighlightService(fids) {
        if (this.highlightService) {
            const cloned = clone(this.highlightService.getConfig());
            const trimmedFids = nnTrim(fids);
            if (trimmedFids) {
                cloned.url = cloned.url + '&fids=' + fids;
            }
            return new LayerServiceConfig(cloned, undefined);
        }
    }

    /**
     *
     * @param timeScale {TimeScaleEnum}
     * @return {ProductConfig}
     */
    getProduct(timeScale = undefined) {
        const prodCode = nnTrim(this.config.prodCode);
        if (prodCode && prodCode.indexOf('{0}')<0)
            return this.allProducts[prodCode];
        return prodCode
            ? (timeScale ? this.allProducts[prodCode.format([timeScale.getCode()])] : this.allProducts[prodCode])
            : undefined;
    }

    /**
     *
     * @return {Array.<ProductConfig>}
     */
    getDataSeriesProducts() {
        return this.config.dataSeriesProdCodes?this.config.dataSeriesProdCodes.map(code=>{
            const prodCode = nnTrim(code);
            if (prodCode && prodCode.indexOf('{0}')<0)
                return [this.allProducts[prodCode]].filter(p=>!!p);
            return this.getTimeScales().map(ts=>this.allProducts[prodCode.format([ts.getCode()])]).filter(p=>!!p)
        }).flat():[];
    }

    /**
     * This layer's min zoom level
     * @return {number} (integer)
     */
    getMinZoomLevel() {
        return this.config.minZoomLevel;
    }

    /**
     * This layer's max zoom level
     * @return {number} (integer)
     */
    getMaxZoomLevel() {
        return this.config.maxZoomLevel;
    }

    /**
     * Get the cluster size in meters. If present, vector layer features will be clustered if they are closer than this distance
     * @return {*} (integer)
     */
    getClusterSizePixels() {
        return this.config.clusterSizePixels;
    }

    /**
     * This layer's styles codes
     * @return {Array<string>}
     */
    getStyleCodes() {
        return this.config.styles;
    }

    /**
     * This layer's chart style code
     * @return string
     */
    getChartStyleCode() {
        return nnTrim(this.config.chartStyle, this.getStyleCodes()[0]);
    }

    /**
     * This layer's style for the accumulated chart
     * @return {string}
     */
    getAccumulatedStyleCode() {
        return this.config.accumulatedStyle;
    }

    /**
     * This layer's styles
     * @return {Array<StyleConfig>}
     */
    getStyles() {
        const styles = [];
        const codes = this.getStyleCodes();
        if (codes) {
            for (let s in codes) {
                const style = this.allStyles[codes[s]];
                if (style) {
                    styles.push(style);
                }
            }
        }
        return styles;
    }

    /**
     * This layer's chart style
     * @return {StyleConfig}
     */
    getChartStyle(){
        return this.allStyles[this.getChartStyleCode()];
    }

    /**
     * This layer's style for the accumulated chart
     * @return {StyleConfig}
     */
    getAccumulatedStyle() {
        if (this.getAccumulatedStyleCode()) {
            const style = this.allStyles[this.getAccumulatedStyleCode()];
            if (style) {
                return style;
            }
        }
    }

    /**
     * This layer's current time code
     * @return {string} (YYYY-MM-DD)
     */
    getTimeCode() {
        return this.config.timeCode;
    }

    /**
     * This layer's time group tag
     * Used for grouping layers (independent of classifiers) that will be time synchronous
     * @return {string}
     */
    getTimeTag() {
        return nnTrim(this.config.timeTag);
    }

    getChartMaster(){
        return nnTrim(this.config.chartMaster);
    }

    clearTimeTag() {
        delete this.config.timeTag;
        return this;
    }

    /**
     * This layer's current time scale
     * @return {TimeScaleEnum}
     */
    getTimeScale() {
        const possibleTimescales = this.getTimeScales().filter(ts => {
            const product = this.getProduct(ts);
            return product && !product.isDisabled();
        });
        if (this.config.timeScale) {
            const forCode = TimeScaleEnum.getForCode(this.config.timeScale, this.isTimescaleLead());
            if (!possibleTimescales || possibleTimescales.indexOf(forCode) >= 0) {
                return forCode;
            }
        }
        if (possibleTimescales && possibleTimescales.length > 0) {
            return this.getTimeScales()[0];
        }
        return TimeScaleEnum.getForCode(undefined, this.isTimescaleLead());
    }

    /**
     * This layer's chart type
     * @return {IndicatorChartTypeEnum}
     */
    getChartType() {
        return IndicatorChartTypeEnum.getForCode(this.config.chartType);
    }

    /**
     * This layer's identify type
     * @return {LayerIdentifyType}
     */
    getIdentifyType() {
        return LayerIdentifyType.getForCode(this.config.identify);
    }

    /**
     * If true, the indicator of this layer is "playable"
     * @return {boolean}
     */
    isPlayable() {
        return this.config.playable === true;
    }

    /**
     * The services driven by this layer. These services may or may not be added as independent layers in the layer tree
     * @return {Array<DrivenServiceConfig>}
     */
    getDrivenServices() {
        if (!this.drivenServices) {
            this.drivenServices = (this.config.drivenServices ? this.config.drivenServices : [])
                .map(ds => new DrivenServiceConfig(ds, this.allServices, this.allStyles));
        }
        return this.drivenServices;
    }

    /**
     * Number of decimal places to round to when showing values to the user
     * @return {Number} (integer)
     */
    getNrOfDecimals() {
        return this.config.nrOfDecimals;
    }

    /**
     * This layer's units
     * @return {string}
     */
    getUnits() {
        return nnTrim(this.config.units) ? this.config.units : undefined;
    }

    /**
     * If true, the render will be high-res (default is true)
     * @return {boolean}
     */
    isHIDPI() {
        return this.config.hidpi === null || this.config.hidpi === undefined || this.config.hidpi === true;
    }

    isDisabled() {
        if (this.config.disabled === true || this.config.disabled === false)
            return this.config.disabled;
        const timeScales = this.getTimeScales();
        return timeScales && timeScales.length > 0
            ? timeScales.filter(t => this.getProduct(t).isDisabled()).length === timeScales.length
            : nn(this.getProduct()?.isDisabled(),false);
    }

    getTimeScales() {
        const timeScales = this.config.timeScales;
        if (timeScales) {
            let res = [];
            const isLead = nnTrim(timeScales[0], '').startsWith('L');
            for (let ts in timeScales) {
                const timeScale = TimeScaleEnum.getForCode(timeScales[ts], isLead);
                if (timeScale && res.indexOf(timeScale) < 0)
                    res.push(timeScale);
            }
            return res.filter(t => !this.getProduct(t).isDisabled());
        } else return nn(this.getService()?.getTimeScales()?.filter(t => !this.getProduct(t).isDisabled()),[]);
    }

    isTimescaleLead() {
        return this.hasTimeScale() && this.getTimeScales()[0].isLead();
    }

    hasTimeScale() {
        return this.getTimeScales().length > 0;
    }

    override(runtimeConfig) {
        super.override(runtimeConfig);
        const layerTimeCode = runtimeConfig.getLayerTimeCode(this.getCode());
        if (layerTimeCode !== undefined) {
            const timeStamp = isString(layerTimeCode.ts)
                ? new Date(layerTimeCode.ts)
                : layerTimeCode.ts;
            if (new Date() - timeStamp < 1000 * 60 * 60 * 8) { //timeCode definitions expire after 8 hours
                this.config.timeCode = layerTimeCode.tc;
            }
        }
        if (runtimeConfig.getLayerTimeScale(this.getCode()) !== undefined) {
            this.config.timeScale = runtimeConfig.getLayerTimeScale(this.getCode());
        }

        if (runtimeConfig.getLayerVisibility(this.getCode()) !== undefined) {
            this.config.visible = runtimeConfig.getLayerVisibility(this.getCode());
        }

        if (runtimeConfig.getLayerOpacity(this.getCode()) !== undefined) {
            this.config.opacity = runtimeConfig.getLayerOpacity(this.getCode());
        }
    }

    verifyTimeScale(errors) {
        const tsCode = nnTrim(this.config.timeScale);
        if (tsCode) {
            const possibleTimescales = this.getTimeScales();
            if (!possibleTimescales.filter(t => t.getCode() !== tsCode).length === 0) {
                errors.push(this.getLogId() + ': invalid timeScale "' + tsCode + '". Possible values: ' + possibleTimescales.map(t => t.getCode()));
            }

        }
        const noProdCode = !this.config.prodCode || this.config.prodCode.length === 0;
        if (!noProdCode) {
            const timeScales = this.getTimeScales();
            if (timeScales.length === 0) {
                if (!this.getProduct())
                    errors.push(this.getLogId() + ": No product for code " + this.config.prodCode);
            } else {
                timeScales
                    .filter(ts => !this.getProduct(ts))
                    .map(ts => this.getLogId() + ": No product for code " + this.config.prodCode.format([ts.getCode()]))
                    .forEach(e => errors.push(e));
            }
        }

    }

    verifyService(errors) {
        const service = this.getService();
        if (!service) {
            errors.push(this.getLogId() + ': No service for layer. Missing service code: ' + this.config.srvCode);
        }
    }

    verifyDataSeriesServices(errors) {
        (this.config.dataSeriesServCodes?this.config.dataSeriesServCodes:[]).forEach(code=>{
            const srvCode = nnTrim(code);
            if(srvCode) {
                if (!this.allServices[srvCode])
                    errors.push(this.getLogId() + ': No data series service for layer. Missing service code: ' + srvCode);
            }
        });
    }

    verifyProduct(errors) {
        const noProdCode = !this.config.prodCode || this.config.prodCode.length === 0;
        if (noProdCode) {
            errors.push(this.getLogId() + ': No product for layer');
        }
    }

    verifyDataSeriesProducts(errors) {
        (this.config.dataSeriesProdCodes?this.config.dataSeriesProdCodes:[]).forEach(code=>{
            const prodCode = nnTrim(code);
            if(prodCode) {
                if (prodCode.indexOf('{0}')<0) {
                    if (!this.allProducts[prodCode])
                        errors.push(this.getLogId() + ': No data series product for layer. Missing product code: ' + prodCode);
                } else {
                    const timeScales = this.getTimeScales();
                    if(timeScales){
                        timeScales.forEach(ts=> {
                            const productConfig = this.allProducts[prodCode.format(ts.getCode())];
                            if(!productConfig)
                                errors.push(this.getLogId() + ': No data series product for layer. Missing product code: ' + prodCode);
                        })
                    }
                }
            }
        })
    }

    verifyStyles(errors) {
        const styleCodes = this.getStyleCodes();
        for (let s in styleCodes) {
            if (!this.allStyles[styleCodes[s]]) {
                errors.push(this.getLogId() + ': no style for code "' + styleCodes[s] + '"');
            }
        }
        const chartStyleCode = this.getChartStyleCode();
        if (!this.allStyles[chartStyleCode]) {
            errors.push(this.getLogId() + ': no style (chart) for code "' + chartStyleCode + '"');
        }
    }

    verifyChartType(errors) {
        if (this.config.chartType && this.config.chartType.length > 0
            && !IndicatorChartTypeEnum.exists(this.config.chartType)) {
            errors.push(this.getLogId() + ": invalid chartType. Possible values: " + IndicatorChartTypeEnum.getPossibleCodes());
        }
    }

    verify() {
        let errors = super.verify(this);

        if (this.config.deviceVisibility && this.config.deviceVisibility.length > 0
            && !DeviceVisibilityEnum.exists(this.config.deviceVisibility)) {
            errors.push(this.getLogId() + ": invalid deviceVisibility. Possible values: " + DeviceVisibilityEnum.getPossibleCodes());
        }

        this.verifyChartType(errors);

        if (this.config.nrOfDecimals !== undefined && this.config.nrOfDecimals !== null &&
            (!Number.isInteger(this.config.nrOfDecimals) || this.config.nrOfDecimals < 0 || this.config.nrOfDecimals > 10)) {
            errors.push(this.getLogId() + ': nrOfDecimals should be an integer between 0 and 10');
        }

        this.verifyProduct(errors);

        this.verifyDataSeriesProducts(errors)

        this.verifyService(errors);

        this.verifyDataSeriesServices(errors)

        const chartMasterCode = this.getChartMaster();
        if(chartMasterCode){
            if(chartMasterCode===this.getCode())
                errors.push(this.getLogId()+": The chartMaster cannot be the same layer")
            else if(!LayerConfig.LAYER_CODES.has(chartMasterCode))
                errors.push(this.getLogId()+": The chartMaster must be an existing layer code")
        }

        this.verifyTimeScale(errors);

        if (this.config.timeScales && this.config.timeScales.length > 0) {
            const isLead = this.config.timeScales[0].startsWith('L');
            for (let ts in this.config.timeScales) {
                if (!TimeScaleEnum.exists(this.config.timeScales[ts], isLead)) {
                    errors.push(this.getLogId() + ": invalid timeScale: " + this.config.timeScales[ts] + '. Possible values: ' + TimeScaleEnum.getPossibleCodes(isLead));
                }
            }
        }

        if (this.getMinZoomLevel() && this.getMaxZoomLevel()
            && this.getMaxZoomLevel() < this.getMinZoomLevel()) {
            errors.push(this.getLogId() + ": invalid min/max zoom levels");
        }

        if (this.getClusterSizePixels() && (
            !isNumber(this.getClusterSizePixels()) ||
            this.getClusterSizePixels() < 10 ||
            this.getClusterSizePixels() > 200
        )) {
            errors.push(this.getLogId() + ": invalid clusterSizePixels. Should be >10 and <200");
        }

        this.verifyStyles(errors);

        const accumulatedStyleCode = this.getAccumulatedStyleCode();
        if (accumulatedStyleCode && !this.allStyles[accumulatedStyleCode])
            errors.push(this.getLogId() + ': no style for code (accumulated) "' + accumulatedStyleCode + '"');

        const drivenServices = this.getDrivenServices();
        for (let ds in drivenServices) {
            const drivenServiceErrors = drivenServices[ds].verify();
            for (let e in drivenServiceErrors) {
                errors.push(this.getLogId() + ": " + drivenServiceErrors[e])
            }
        }

        return errors;
    }
}

/**
 * A service driven by another layer
 * @extends CodeConfig
 */
class DrivenServiceConfig extends Config {
    /**
     *
     * @param config {Object} The config object
     * @param allServices {Object<String,LayerServiceConfig>} All the available layer services
     * @param allStyles {Object<String,StyleConfig>} All the available styles
     */
    constructor(config, allServices, allStyles) {
        super(config);
        this.service = allServices[this.getSrvCode()];
        this.allStyles = allStyles;
    }

    getClassName() {
        return "DrivenServiceConfig";
    }

    getSrvCode() {
        return this.config.srvCode;
    }

    _getStyleCode() {
        return this.config.styleCode;
    }

    /**
     * The style for the driven service
     * @return {StyleConfig|undefined}
     */
    getStyle() {
        return this.config.styleCode ? this.allStyles[this.config.styleCode] : undefined;
    }

    /**
     * The drive service
     * @return {LayerServiceConfig}
     */
    getService() {
        return this.service;
    }

    verify() {
        const errors = super.verify();

        if (!this.service)
            errors.push(this.getLogId() + ': service "' + this.getSrvCode() + '" not found.');

        if (this._getStyleCode()) {
            if (!this.getStyle()) {
                errors.push(this.getLogId() + ': style "' + this._getStyleCode() + '" not found.');
            }
        }
        return errors;
    }
}

/**
 * Product information
 * @extends CodeNameInfoConfig
 * @hideconstructor
 */
class ProductConfig extends CodeNameInfoConfig {

    /**
     *
     * @param config {Object} The config object
     * @param scaleId {string} The scale id param for indicator download
     */
    constructor(config, scaleId) {
        super(config);
        this.disabled = false;
        this.missingTimesteps = new Set();
        this.provisionalTimesteps = new Set();
        this.deficientTimesteps =  new Set();
        this.scaleId = scaleId;
        this.overrider = new ConfigOverride(config.overrides, [
            'name',
            'info',
            'citationStatement',
            'publication',
            'organisation',
            'partnerOrganisation',
            'license',
            'dataCatalogueUrl',
            'factsheetUrl'
        ]);
    }

    getClassName() {
        return "ProductConfig";
    }

    disable() {
        this.disabled = true;
        return this;
    }

    enable() {
        this.disabled = false;
        return this;
    }

    isDisabled() {
        return this.disabled;
    }

    /**
     * Is a timestep missing?
     * @param timestep {Date|String}
     * @return {boolean}
     */
    isMissing(timestep){
        timestep = dateFromStr(timestep);
        return this.missingTimesteps.has(timestep.getTime());
    }

    /**
     * Remove all missing timesteps
     */
    clearMissingTimesteps() {
        this.missingTimesteps.clear();
    }

    /**
     * Add timesteps to be marked as missing
     * @param timesteps {Array.<Date|string>}
     */
    addMissingTimesteps(timesteps) {
        timesteps.map(d=>dateFromStr(d)).forEach(d=>this.missingTimesteps.add(d.getTime()));
    }

    /**
     * Is a timestep provisional?
     * @param timestep {Date|String}
     * @return {boolean}
     */
    isProvisional(timestep){
        timestep = dateFromStr(timestep);
        return this.provisionalTimesteps.has(timestep.getTime());
    }

    /**
     * Remove all provisional timesteps
     */
    clearProvisionalTimesteps() {
        this.provisionalTimesteps.clear();
    }

    /**
     * Add timesteps to be marked as provisional
     * @param timesteps {Array.<Date|string>}
     */
    addProvisionalTimesteps(timesteps) {
        timesteps.map(d=>dateFromStr(d)).forEach(d=>this.provisionalTimesteps.add(d.getTime()));
    }

    /**
     * Is a timestep deficient?
     * @param timestep {Date|String}
     * @return {boolean}
     */
    isDeficient(timestep){
        timestep = dateFromStr(timestep);
        return this.deficientTimesteps.has(timestep.getTime());
    }

    /**
     * Remove all deficient timesteps
     */
    clearDeficientTimesteps() {
        this.deficientTimesteps.clear();
    }


    /**
     * Add timesteps to be marked as deficient
     * @param timesteps {Array.<Date|string>}
     */
    addDeficientTimesteps(timesteps) {
        timesteps.map(d=>dateFromStr(d)).forEach(d=>this.deficientTimesteps.add(d.getTime()));
    }

    /**
     * The scale id param value to pass to the download data service
     * @return {string}
     */
    getScaleId() {
        return this.scaleId;
    }

    /**
     * This layer's start date
     * @return {string} (YYYY-MM-DD)
     */
    getDataFrom() {
        const periodicityEnum = this.getPeriodicity();
        if (periodicityEnum) {
            const startDayDate = dateFromStr(!nnTrim(this.config.startDay)
                ? this.config.endDay
                : this.config.startDay);
            const timeCalculator = new TimeCalculator(periodicityEnum);
            return timeCalculator.writeTimeCodeFromDate(startDayDate);
        }
    }

    /**
     * This layer's end date
     * @return {string} (YYYY-MM-DD)
     */
    getDataUntil() {
        if (!nnTrim(this.config.endDay))
            return undefined;
        const periodicityEnum = this.getPeriodicity();
        if (periodicityEnum) {
            const timeCalculator = new TimeCalculator(periodicityEnum);
            return timeCalculator.writeTimeCodeFromDate(this.config.endDay);
        }
    }

    getName(date = undefined) {
        return this.overrider.getValueForProperty('name', date, super.getName());
    }

    getTimescaleToken() {
        return nnTrim(this.config.timescaleToken);
    }


    /**
     *
     * @return {string}
     */
    getInfo(date = undefined) {
        return this.overrider.getValueForProperty('info', date, super.getInfo());
    }

    /**
     * This layer's data catalogue URL
     * @return {object}
     */
    getDataCatalogueUrl(date = undefined) {
        return this.overrider.getValueForProperty('dataCatalogueUrl', date, this.config.dataCatalogueUrl);
    }

    /**
     *
     * @return {string}
     */
    getLicense(date = undefined) {
        return this.overrider.getValueForProperty('license', date, this.config.license);
    }

    /**
     *
     * @return {string}
     */
    getCitationStatement(date = undefined) {
        return this.overrider.getValueForProperty('citationStatement', date, this.config.citationStatement);
    }

    /**
     *
     * @return {Object}
     */
    getOrganisation(date = undefined) {
        return this.overrider.getValueForProperty('organisation', date, this.config.organisation);
    }

    /**
     *
     * @return {string}
     */
    getOrganisationName(date = undefined) {
        const organisation = this.getOrganisation(date);
        return organisation ? organisation.name : undefined;
    }

    /**
     *
     * @return {string}
     */
    getOrganisationEmail(date = undefined) {
        const organisation = this.getOrganisation(date);
        return organisation ? organisation.email : undefined;
    }

    /**
     *
     * @return {Object}
     */
    getPartnerOrganisation(date = undefined) {
        return this.overrider.getValueForProperty('partnerOrganisation', date, this.config.partnerOrganisation);
    }

    /**
     *
     * @return {string}
     */
    getPartnerOrganisationName(date = undefined) {
        const partnerOrganisation = this.getPartnerOrganisation(date);
        return partnerOrganisation ? partnerOrganisation.name : undefined;
    }

    /**
     *
     * @return {string}
     */
    getPartnerOrganisationEmail(date = undefined) {
        const partnerOrganisation = this.getPartnerOrganisation(date);
        return partnerOrganisation ? partnerOrganisation.email : undefined;
    }

    /**
     *
     * @return {string}
     */
    getPartnerOrganisationURL(date = undefined) {
        const partnerOrganisation = this.getPartnerOrganisation(date);
        return partnerOrganisation ? partnerOrganisation.url : undefined;
    }

    /**
     *
     * @return {string}
     */
    getRefPublication(date = undefined) {
        return this.overrider.getValueForProperty('refPublication', date, this.config.refPublication);
    }

    getPublicationTitle(date = undefined) {
        const publication = this.getPublication(date);
        return publication ? publication.title : undefined;
    }

    getPublicationAuthors(date = undefined) {
        const publication = this.getPublication(date);
        return publication ? publication.authors : undefined;
    }

    getPublicationPublisher(date = undefined) {
        const publication = this.getPublication(date);
        return publication ? publication.publisher : undefined;
    }

    getPublicationYear(date = undefined) {
        const publication = this.getPublication(date);
        return publication ? publication.year : undefined;
    }

    getPublicationUrl(date = undefined) {
        const publication = this.getPublication(date);
        return publication ? publication.href : undefined;
    }

    getPublication(date = undefined) {
        return this.overrider.getValueForProperty('publication', date, this.config.publication);
    }

    getFactSheetUrl(date = undefined) {
        return this.overrider.getValueForProperty('factsheetUrl', date, this.config.factsheetUrl);
    }

    getDownloadPageUrl() {
        return this.config.downloadPageUrl;
    }

    /**
     * This layer's periodicity (monthly, yearly, etc.)
     * @return {PeriodicityEnum}
     */
    getPeriodicity() {
        return this.config.frequency ? PeriodicityEnum.getForCode(this.config.frequency) : undefined;
    }

    verify() {
        const errors = super.verify();

        this.overrider.verify().forEach(e => errors.push(this.getLogId() + ': ' + e));

        const nameOverrides = this.overrider.getAllValuesForProperty('name');
        for (let i = 0; i < nameOverrides.length; i++) {
            const nameError = this.verifyName(nameOverrides[i]);
            if (nameError)
                errors.push(this.getLogId() + ': override[' + i + ']: ' + nameError)
        }

        const verifyDataCatalogueUrl = (dataCatalogueUrl, overrideIndex = undefined) => {
            if (dataCatalogueUrl) {
                const logId = overrideIndex !== undefined
                    ? this.getLogId() + ': override[' + overrideIndex + ']: '
                    : this.getLogId() + ': ';
                if (!dataCatalogueUrl.name || dataCatalogueUrl.name.length === 0) {
                    errors.push(logId + "dataCatalogueUrl.name is mandatory");
                }
                if (!dataCatalogueUrl.url || dataCatalogueUrl.url.length === 0) {
                    errors.push(logId + "dataCatalogueUrl.url is mandatory");
                } else if (!verifyUrl(dataCatalogueUrl.url)) {
                    errors.push(logId + "dataCatalogueUrl.url is invalid");
                }
            }
        }
        verifyDataCatalogueUrl(this.getDataCatalogueUrl());
        const dataCatalogueOverrides = this.overrider.getAllValuesForProperty('dataCatalogueUrl');
        for (let i = 0; i < dataCatalogueOverrides.length; i++) {
            verifyDataCatalogueUrl(dataCatalogueOverrides[i], i);
        }

        const verifyPublication = (publication, overrideIndex = undefined) => {
            if (publication) {
                const logId = overrideIndex !== undefined
                    ? this.getLogId() + ': override[' + overrideIndex + ']: '
                    : this.getLogId() + ': ';
                if (!nnTrim(publication.title))
                    errors.push(logId + "publication.title is mandatory");

                if (nnTrim(publication.href) && !verifyUrl(publication.href))
                    errors.push(logId + "publication.href is invalid");
            }
        }
        verifyPublication(this.getPublication());
        const publicationOverrides = this.overrider.getAllValuesForProperty('publication');
        for (let i = 0; i < publicationOverrides.length; i++) {
            verifyPublication(publicationOverrides[i], i);
        }

        if (nnTrim(this.config.frequency) && !PeriodicityEnum.exists(this.config.frequency)) {
            errors.push(this.getLogId() + ": invalid frequency. Possible values: " + PeriodicityEnum.getPossibleCodes());
        }

        const periodicity = this.getPeriodicity();
        if (periodicity) {
            if (periodicity !== PeriodicityEnum.LATEST && !this.getDataFrom())
                errors.push(this.getLogId() + ": startDay is mandatory");
            if (this.getDataFrom() && !verifyDateString(this.getDataFrom()))
                errors.push(this.getLogId() + ": invalid startDay (" + this.getDataFrom() + ")");
            else if (this.getDataUntil() && !verifyDateString(this.getDataUntil()))
                errors.push(this.getLogId() + ": invalid endDay (" + this.getDataUntil() + ")");

            if (this.getDataFrom() && this.getDataUntil()) {
                let dataUntil = dateFromStr(this.getDataUntil());
                let dataFrom = dateFromStr(this.getDataFrom());
                if (dataUntil.getTime() < dataFrom.getTime()) {
                    errors.push(this.getLogId() + ": endDay must be later than startDay");
                }
            }
        }

        return errors;
    }
}

/**
 * The service for any layers
 * @extends CodeConfig
 */
class LayerServiceConfig extends CodeConfig {

    /**
     *
     * @param config
     * @param allServiceWarningMessages {Object<String,Array<ServiceWarningMessageConfig>>} All the service warning messages by service code
     */
    constructor(config, allServiceWarningMessages) {
        super(config);
        this.warningMessages = allServiceWarningMessages && allServiceWarningMessages[this.getCode()]
            ? allServiceWarningMessages[this.getCode()]
            : [];
        this.overrider = new ConfigOverride(config.overrides, [
            'url',
            'tileSize'
        ]);

    }

    getClassName() {
        return "LayerServiceConfig";
    }

    /**
     *
     * @return {Array<ServiceWarningMessageConfig>}
     */
    getWarningMessages() {
        return this.warningMessages;
    }

    /**
     * This layer's extent
     * @return {Array<number>}
     */
    getExtentWgs84() {
        return this.config.extent;
    }

    /**
     * This layer's protocol (WMS, WFS, etc)
     * @return {ProtocolEnum}
     */
    getProtocol() {
        return ProtocolEnum.getForCode(this.config.protocol);
    }

    /**
     * This layer's URL
     * @return {string}
     */
    getUrl(date = undefined) {
        const service = this.overrider.getValueForProperty('service', date, this.config.service);
        if (
            service
            && service.uri
            && service.uri.authority
            && service.args
            && service.args.fix
            && service.args.fix['LAYERS']
        ) {
            let url = tumbo.serviceUrlPrefix
                ? (tumbo.serviceUrlPrefix.indexOf("http") === 0 ? '' : '//') + tumbo.serviceUrlPrefix
                : (service.uri.scheme ? service.uri.scheme + '://' : '') + service.uri.authority.host;
            url += '/' + service.uri.path + '?';
            if (service.args.fix.map) //strangely, the order of the params matters for the server. map must be the first one
                url += 'map=' + service.args.fix.map;
            url += '&LAYERS=' + service.args.fix['LAYERS'];
            return url;
        } else {
            const url = this.overrider.getValueForProperty('url', date, this.config.url);
            if (url) {
                return prefixUrl(url);
            }
        }
    }

    /**
     * The size of the tiles to retrieve from the service.
     * If this is not a WMS raster layer, this will be ignored.
     * @return {number} (integer)
     */
    getTileSize(date = undefined) {
        return this.overrider.getValueForProperty('tileSize', date, this.config.tileSize);
    }

    /**
     * Is this layer clickable? Only applies to vector layers
     * @return {boolean}
     */
    isClickable() {
        return !!this.config.clickable;
    }

    /**
     * This layer's type (vector, raster)
     * @return {LayerTypeEnum}
     */
    getLayerType() {
        return LayerTypeEnum.getForCode(this.config.layerType);
    }

    /**
     * The original CRS of the data that comes from the server.
     * Ignored if layerType!=="VECTOR"
     * @return {string|undefined}
     */
    getOriginalCRS() {
        return this.config.originalCRS;
    }

    /**
     * This layer's time param name.
     * If set, the GIS request will use this param name to send the time code
     * If not set, the GIS request will use the SELECTED_* (year, month, day, week) to send the time
     * @return {string}
     */
    getTimeSelector() {
        return this.config.timeSelector;
    }

    /**
     * This layer's available time scales
     * @return {Array<TimeScaleEnum>}
     */
    getTimeScales() {
        let res = [];
        const timeScales = this.config.timeScales;
        if (timeScales) {
            const isLead = nnTrim(timeScales[0], '').startsWith('L');
            for (let ts in timeScales) {
                const timeScale = TimeScaleEnum.getForCode(timeScales[ts], isLead);
                if (timeScale && res.indexOf(timeScale) < 0)
                    res.push(timeScale);
            }
        }
        return res;
    }

    isTimescaleLead() {
        return this.hasTimeScale() && this.getTimeScales()[0].isLead();
    }

    hasTimeScale() {
        return this.getTimeScales().length > 0;
    }

    verify() {
        let errors = super.verify(this);

        if (this.getExtentWgs84()) {
            if (!verifyExtentLonLatWgs84(this.getExtentWgs84())) {
                errors.push(this.getLogId() + ': invalid extent. Should be [minLon, minLat, maxLon, maxLat] in WGS84');
            }
        }

        this._verifyProtocol(errors);

        const url = this.getUrl();
        if (!url || url.length === 0) {
            errors.push(this.getLogId() + ": url is mandatory");
        }

        this._verifyLayerType(errors);

        if (this.config.timeScales && this.config.timeScales.length > 0) {
            const isLead = this.config.timeScales[0].startsWith('L');
            for (let ts in this.config.timeScales) {
                if (!TimeScaleEnum.exists(this.config.timeScales[ts], isLead)) {
                    errors.push(this.getLogId() + ": invalid timeScale: " + this.config.timeScales[ts] + '. Possible values: ' + TimeScaleEnum.getPossibleCodes(isLead));
                }
            }
        }
        return errors;
    }

    _verifyProtocol(errors) {
        if (!nnTrim(this.config.protocol)) {
            errors.push(this.getLogId() + ": protocol is mandatory");
        } else if (!ProtocolEnum.exists(this.config.protocol)) {
            errors.push(this.getLogId() + ": invalid protocol. Possible values: " + ProtocolEnum.getPossibleCodes());
        }
    }

    _verifyLayerType(errors) {
        if (!nnTrim(this.config.layerType)) {
            errors.push(this.getLogId() + ": layerType is mandatory");
        } else if (!LayerTypeEnum.exists(this.config.layerType)) {
            errors.push(this.getLogId() + ": invalid layerType. Possible values: " + LayerTypeEnum.getPossibleCodes());
        }
    }
}

/**
 * The config of an element of the layer list
 * @extends CodeUniqueNameInfoConfig
 * @hideconstructor
 */
class LayerListElementConfig extends CodeUniqueConfig {

    /**
     * The type of the element
     * @return {LayerGroupTypeEnum}
     */
    getType() {
        return LayerGroupTypeEnum.getForCode(this.config.type);
    }

    /**
     * Device visibility of this element
     * @return {DeviceVisibilityEnum}
     */
    getDeviceVisibility() {
        return DeviceVisibilityEnum.getForCode(this.config.deviceVisibility);
    }

    /**
     * This element's children (layers, only. base or context)
     * @param includeDisabled {Boolean} if true, includes disabled children
     * @return {Array<LayerListElementConfig>}
     */
    getChildren(includeDisabled = false) {
        return [];
    }

    /**
     * Is tree element disabled?
     * @return {*}
     */
    isDisabled() {
        return this.config.disabled;
    }

    verify() {
        let errors = super.verify(this);

        if (!this.config.type || this.config.type.length === 0) {
            errors.push(this.getLogId() + ": type is mandatory");
        } else if (!LayerGroupTypeEnum.exists(this.config.type)) {
            errors.push(this.getLogId() + ": invalid type. Possible values: " + LayerGroupTypeEnum.getPossibleCodes());
        }

        if (this.config.deviceVisibility && this.config.deviceVisibility.length > 0
            && !DeviceVisibilityEnum.exists(this.config.deviceVisibility)) {
            errors.push(this.getLogId() + ": invalid deviceVisibility. Possible values: " + DeviceVisibilityEnum.getPossibleCodes());
        }

        if (this.config.disabled !== null && this.config.disabled !== undefined) {
            if (!isBoolean(this.config.disabled))
                errors.push(this.getLogId() + ": disabled must be a boolean");
        }

        return errors;
    }
}

/**
 * A base layer configuration
 * @extends LayerListElementConfig
 */
class BaseLayerConfig extends LayerListElementConfig {

    /**
     *
     * @param config {Object} The config object
     * @param service {LayerServiceConfig} The layer service
     * @param highlightService {LayerServiceConfig} The layer service for getting features to show as highlights
     * @param product {ProductConfig} The products
     * @param allStyles {Object<String,StyleConfig>} All the available styles
     */
    constructor(config, service, highlightService, product, allStyles) {
        super(config);
        this.service = service;
        this.highlightService = highlightService;
        this.product = product;
        this.allStyles = allStyles;
    }

    getClassName() {
        return "BaseLayerConfig";
    }

    clone(code) {
        const clonedConfig = clone(this.config);
        if(code)
            clonedConfig.code = code;
        return new this.__proto__.constructor(clonedConfig, this.service, this.highlightService, this.product, this.allStyles);
    }

    /**
     * The map background colour to set when this layer is visible
     * @return {string|undefined}
     */
    getMapBackgroundColour() {
        return nnTrim(this.config.mapBackgroundColor) ? nnTrim(this.config.mapBackgroundColor) : '#FFFFFF';
    }

    /**
     *
     * @return {LayerServiceConfig}
     */
    getService() {
        return this.service;
    }

    /**
     * @fids {string|undefined} A comma separated list of integer feature ids
     * @return {LayerServiceConfig}
     */
    getHighlightService(fids) {
        if (this.highlightService) {
            const cloned = clone(this.highlightService.getConfig());
            const trimmedFids = nnTrim(fids);
            if (trimmedFids) {
                cloned.url = cloned.url + '&fids=' + fids;
            }
            return new LayerServiceConfig(cloned, undefined);
        }
    }

    /**
     *
     * @return {ProductConfig}
     */
    getProduct() {
        return this.product;
    }

    /**
     * Is this layer visible?
     * @return {boolean}
     */
    isVisible() {
        return this.config.visible === true;
    }

    /**
     * This layer's min zoom level
     * @return {number} (integer)
     */
    getMinZoomLevel() {
        return this.config.minZoomLevel;
    }

    /**
     * This layer's max zoom level
     * @return {number} (integer)
     */
    getMaxZoomLevel() {
        return this.config.maxZoomLevel;
    }

    /**
     * This layer's styles
     * @return {Array<string>}
     */
    getStyleCodes() {
        return this.config.styles;
    }

    /**
     * This layer's styles
     * @return {Array<StyleConfig>}
     */
    getStyles() {
        const styles = [];
        if (this.config.styles) {
            for (let s in this.config.styles) {
                const style = this.allStyles[this.config.styles[s]];
                if (style) {
                    styles.push(style);
                }
            }
        }
        return styles;
    }

    /**
     * If true, this layer is available for the identify tool
     * @return {boolean}
     */
    isIdentifiable() {
        return this.config.identifiable === true;
    }

    /**
     * The render ordinal. If defined, overrides the layer tree ordinal
     * @return {number|undefined}
     */
    getRenderOrdinal() {
        return toNumber(this.config.renderOrdinal);
    }

    /**
     * If true, the render will be high-res (default is true)
     * @return {boolean}
     */
    isHIDPI() {
        return this.config.hidpi === null || this.config.hidpi === undefined || this.config.hidpi === true;
    }

    override(runtimeConfig) {
        if (runtimeConfig.getBaseLayerVisibility(this.getCode()) !== undefined) {
            this.config.visible = runtimeConfig.getBaseLayerVisibility(this.getCode());
        }
    }

    verify() {
        let errors = super.verify();

        if (!this.getService()) {
            errors.push(this.getLogId() + ': No service for code ' + this.config.srvCode);
        }

        if (!this.config.prodCode || this.config.prodCode.length === 0) {
            errors.push(this.getLogId() + ': No product for layer');
        } else {
            if (!this.product)
                errors.push(this.getLogId() + ': No product for code ' + this.config.prodCode);
        }

        if (this.config.deviceVisibility && this.config.deviceVisibility.length > 0
            && !DeviceVisibilityEnum.exists(this.config.deviceVisibility)) {
            errors.push(this.getLogId() + ": invalid deviceVisibility. Possible values: " + DeviceVisibilityEnum.getPossibleCodes());
        }

        if (this.getMinZoomLevel() && this.getMaxZoomLevel()
            && this.getMaxZoomLevel() < this.getMinZoomLevel()) {
            errors.push(this.getLogId() + ": invalid min/max zoom levels");
        }

        if (this.getMapBackgroundColour()) {
            try {
                if (!parseCSSColor(this.getMapBackgroundColour()))
                    errors.push(this.getLogId() + ": invalid mapBackgroundColor");
            } catch (e) {
                errors.push(this.getLogId() + ": invalid mapBackgroundColor (" + e + ")");
            }
        }

        const styleCodes = this.getStyleCodes();
        for (let s in styleCodes) {
            if (!this.allStyles[styleCodes[s]]) {
                errors.push(this.getLogId() + ': no style for code "' + styleCodes[s] + '"');
            }
        }

        if (this.config.disabled !== null && this.config.disabled !== undefined) {
            if (!isBoolean(this.config.disabled))
                errors.push(this.getLogId() + ": disabled must be a boolean");
        }

        return errors;
    }
}

/**
 * A context layer configuration
 * @extends BaseLayerConfig
 * @hideconstructor
 */
class ContextLayerConfig extends BaseLayerConfig {

    override(runtimeConfig) {
        if (runtimeConfig.getContextLayerVisibility(this.getCode()) !== undefined) {
            this.config.visible = runtimeConfig.getContextLayerVisibility(this.getCode());
        }
    }

    getClassName() {
        return "ContextLayerConfig";
    }
}

/**
 * A projection. Its code should be the EPSG code
 * @extends CodeConfig
 * @hideconstructor
 */
class Projection extends CodeUniqueConfig {
    static MERCATOR_PROJECTION = new Projection({code: MERCATOR_CODE, name: 'Mercator', decimals: 4, unit: 'º'});

    static WGS84_PROJECTION = new Projection({code: WGS84_CODE, name: 'WGS84', decimals: 0, unit: 'm'});

    getClassName() {
        return "Projection";
    }

    /**
     * The nr of decimals to round to. Precision
     * @return {number}
     */
    getDecimals() {
        return this.config.decimals !== undefined ? this.config.decimals : 0;
    }

    /**
     * Units of the projection
     * @return {string}
     */
    getUnit() {
        return this.config.unit ? this.config.unit : 'm';
    }

    /**
     * Name of the projection
     * @return {string}
     */
    getName() {
        return this.config.name;
    }

    /**
     * proj4js definition of the projection. not needed for WGS84 and Mercator
     * @return {string}
     */
    getDefinition() {
        return this.config.definition;
    }

    /**
     * extent of the projection in WGS84. not needed for WGS84
     * tip: take it from epsg.io
     * @return {Array<number>}
     */
    getExtent() {
        return this.config.extent ? this.config.extent : [-180, -90, 180, 90];
    }

    /**
     * The SRID of this projection (whatever is after the ':' in the code
     * @returns {Number} (integer)
     */
    getSRID() {
        return Number(this.getCode().split(':')[1]);
    }

    verify() {
        const errors = super.verify();
        if (!this.getName() || this.getName().length === 0) {
            errors.push(this.getLogId() + ": name is mandatory");
        }
        if (this.getExtent() && !verifyExtentLonLatWgs84(this.getExtent())) {
            errors.push(this.getLogId() + ": invalid WGS84 extent");
        }
        return errors;
    }
}

/**
 * The zoom levels available in the map
 * @extends Config
 * @hideconstructor
 */
class ZoomLevels extends Config {
    getClassName() {
        return "ZoomLevels";
    }

    static getDefaultZoomLevelsForWgs84() {
        return new ZoomLevels({
            "minResolutionMercator": 78271.516953125,
            "levels": 14
        });
    }

    /**
     * Min resolution in the Mercator array of resolutions
     * @return {number}
     */
    getMinResolutionMercator() {
        return this.config.minResolutionMercator;
    }

    /**
     * The nr of zoom levels
     * @return {number} (integer)
     */
    getLevels() {
        return this.config.levels;
    }

    /**
     * The index of the max resolution available in the map, from the array of possible resolutions
     * @return {number} (integer)
     */
    getMaxResolutionIndex() {
        return this.getMinResolutionIndex() + this.getLevels() - 1;
    }

    /**
     * The index of the min resolution available in the map, from the array of possible resolutions
     * @return {number} (integer)
     */
    getMinResolutionIndex() {
        return RESOLUTIONS[MERCATOR_CODE]
            .map(r => Math.round(r))
            .indexOf(Math.round(this.getMinResolutionMercator()));
    }

    /**
     * Given a projection code, get the list of resolutions
     * @param projection {string} The projection code
     * @return {Array<number>}
     */
    getResolutionsForProjection(projection) {
        return RESOLUTIONS[projection];
    }

    /**
     * Given a projection code, get the list of resolutions available for display
     * @param projection {string} The projection code
     * @return {Array<number>}
     */
    getAvailableResolutionsForProjection(projection) {
        return this.getResolutionsForProjection(projection).slice(this.getMinResolutionIndex(), this.getMaxResolutionIndex() + 1)
    }


    /**
     * Given a projection code, get the resolution for a zoom level
     * @param projection {string} The projection code
     * @param zoomLevel {number} (integer)
     * @return {number}
     */
    getResolutionForZoomLevel(projection, zoomLevel) {
        return this.getAvailableResolutionsForProjection(projection)[zoomLevel];
    }

    /**
     * Given a projection code, get the zoom level for a resolution
     * @param projection {string} The projection code
     * @param resolution {number}
     * @return {number} (integer)
     */
    getZoomLevelForResolution(projection, resolution) {
        const zLevels = this.getAvailableResolutionsForProjection(projection);
        let resolutionIndex = 0;
        for (let i = 0; i < zLevels.length && zLevels[i] >= resolution; i++)
            resolutionIndex = i;
        return resolutionIndex;
    }

    /**
     * Is the given resolution a known resolution?
     * @param projection {string} The projection code
     * @param resolution {number}
     * @return {boolean}
     */
    isKnownResolution(projection, resolution) {
        return this.getResolutionIndex(projection, resolution) >= 0;
    }


    /**
     * Given a projection code, get the index of a resolution in the list of resolutions
     * @param projection {string} The projection code
     * @param resolution {number}
     * @return {number} (integer)
     */
    getResolutionIndex(projection, resolution) {
        return this.getAvailableResolutionsForProjection(projection).indexOf(resolution);
    }

    verify() {
        const errors = super.verify();

        const minResolutionMercator = this.getMinResolutionMercator();
        if (minResolutionMercator) {
            if (RESOLUTIONS[MERCATOR_CODE].map(r => Math.round(r)).indexOf(Math.round(minResolutionMercator)) < 0) {
                errors.push(this.getLogId() + ": minResolutionMercator is invalid");
            }
        } else {
            errors.push(this.getLogId() + ": minResolutionMercator is mandatory");
        }

        const levels = this.getLevels();
        if (!levels) {
            errors.push(this.getLogId() + ": levels is mandatory");
        }

        if (minResolutionMercator !== undefined && levels > 0) {
            const expectedZoomLevels = this.getMaxResolutionIndex() - this.getMinResolutionIndex() + 1;
            if (expectedZoomLevels !== levels) {
                errors.push(this.getLogId() + ": There should be (" + expectedZoomLevels + ") zoom levels");
            }
        }

        return errors;
    }
}

export {
    MainConfig,
    IndicatorBasedMainConfig,
    RuntimeConfig,
    LayerTreeElementConfig,
    LayerConfig,
    ProductConfig,
    LayerServiceConfig,
    Projection,
    ZoomLevels,
    LayerListElementConfig,
    BaseLayerConfig,
    ContextLayerConfig
}